import { bellOf, neighborSlot } from '@/utils/teacherContextGrid'

export interface SchoolSlot {
  id: string
  slot_number: number | null
  start_time: string | null
  end_time?: string | null
  is_break?: boolean
  name?: string | null
}

export interface SlotLesson {
  id: string
  time_slot_id?: string | null
  day_of_week?: number | null
  valid_from?: string | null
  valid_to?: string | null
  lesson_number?: number | null
  start_time?: string | null
  subject_name?: string | null
  klass_name?: string | null
  room_name?: string | null
  subject?: string | null
  class_name?: string | null
  room?: string | null
}

export function adjacentSlot(
  slots: SchoolSlot[],
  currentId: string | null | undefined,
  direction: 'up' | 'down',
): SchoolSlot | null {
  if (!currentId) return null
  return neighborSlot(
    slots.map((slot) => ({ ...slot, end_time: slot.end_time ?? null })),
    currentId,
    direction,
  )
}

export function lessonInSlot<T extends SlotLesson>(
  lessons: T[],
  entry: T,
  slotId: string,
): T | null {
  const inSlot = lessons.filter((row) =>
    row.id !== entry.id
    && row.time_slot_id === slotId
    && row.day_of_week === entry.day_of_week
  )
  return inSlot.find((row) =>
    (row.valid_from ?? null) === (entry.valid_from ?? null)
    && (row.valid_to ?? null) === (entry.valid_to ?? null)
  ) ?? inSlot[0] ?? null
}

function field(entry: SlotLesson, a: keyof SlotLesson, b: keyof SlotLesson): string {
  return String(entry[a] ?? entry[b] ?? '').trim()
}

export function cellsComplement(a: SlotLesson, b: SlotLesson): boolean {
  const aSub = field(a, 'subject_name', 'subject')
  const bSub = field(b, 'subject_name', 'subject')
  const aKlass = field(a, 'klass_name', 'class_name')
  const bKlass = field(b, 'klass_name', 'class_name')
  const aRoom = field(a, 'room_name', 'room')
  const bRoom = field(b, 'room_name', 'room')
  if (aSub && bSub && aSub.toLowerCase() !== bSub.toLowerCase()) return false
  if (aKlass && bKlass && aKlass.toLowerCase() !== bKlass.toLowerCase()) return false
  if (aRoom && bRoom && aRoom.toLowerCase() !== bRoom.toLowerCase()) return false
  const aPlace = Boolean(aKlass || aRoom)
  const bPlace = Boolean(bKlass || bRoom)
  return Boolean(aSub) !== Boolean(bSub) || aPlace !== bPlace
}

export function nextLesson<T extends SlotLesson>(
  lessons: T[],
  entry: T,
  direction: 'up' | 'down',
): T | null {
  const ordered = lessons
    .filter((row) => row.day_of_week === entry.day_of_week)
    .sort((a, b) => (a.start_time || '').localeCompare(b.start_time || '') || a.id.localeCompare(b.id))
  const idx = ordered.findIndex((row) => row.id === entry.id)
  if (idx < 0) return null
  const next = direction === 'up' ? idx - 1 : idx + 1
  if (next < 0 || next >= ordered.length) return null
  return ordered[next] ?? null
}

export function fusionNeighbor<T extends SlotLesson>(
  lessons: T[],
  entry: T,
  direction: 'up' | 'down',
  slots?: SchoolSlot[],
): T | null {
  const neighbor = slots && entry.time_slot_id
    ? nextLessonInBell(lessons, entry, direction, slots)
    : nextLesson(lessons, entry, direction)
  if (!neighbor || !cellsComplement(entry, neighbor)) return null
  return neighbor
}

function nextLessonInBell<T extends SlotLesson>(
  lessons: T[],
  entry: T,
  direction: 'up' | 'down',
  slots: SchoolSlot[],
): T | null {
  const bell = bellOf(
    slots.map((slot) => ({ ...slot, end_time: slot.end_time ?? null })),
    entry.time_slot_id ?? '',
  )
  if (!bell) return null
  const order = new Map(bell.map((slot, index) => [slot.id, index]))
  const inBell = lessons
    .filter((row) =>
      row.day_of_week === entry.day_of_week
      && row.time_slot_id != null
      && order.has(row.time_slot_id)
    )
    .sort((a, b) =>
      (order.get(a.time_slot_id ?? '') ?? 0) - (order.get(b.time_slot_id ?? '') ?? 0)
      || a.id.localeCompare(b.id)
    )
  const idx = inBell.findIndex((row) => row.id === entry.id)
  if (idx < 0) return null
  const next = direction === 'up' ? idx - 1 : idx + 1
  if (next < 0 || next >= inBell.length) return null
  return inBell[next] ?? null
}
